import { useEffect, ReactNode } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { cn } from "@/lib/utils";
import { Reservation } from "@/types/table";
import { RESERVATION_DRAG_TYPE, ReservationDragItem } from "./FloorPlanDndProvider";

interface Props {
  reservation: Reservation;
  draggable: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  children: ReactNode;
}

/**
 * Draggable card shell. Uses react-dnd useDrag so it works on touch (TouchBackend)
 * as well as on mouse (HTML5Backend). The empty image hides the native HTML5
 * ghost so we can render our own ReservationDragLayer.
 */
export const V2ReservationCard = ({
  reservation, draggable, onDragStart, onDragEnd,
  className, onMouseEnter, onMouseLeave, onClick, onDoubleClick, children,
}: Props) => {
  const [{ isDragging }, dragRef, preview] = useDrag<
    ReservationDragItem,
    void,
    { isDragging: boolean }
  >(() => ({
    type: RESERVATION_DRAG_TYPE,
    canDrag: draggable,
    item: () => {
      onDragStart();
      return { reservationId: reservation.id };
    },
    end: () => {
      onDragEnd();
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [draggable, reservation.id, onDragStart, onDragEnd]);

  useEffect(() => {
    // Hide the default HTML5 drag image — we render ReservationDragLayer instead.
    // (Touch backend ignores this.)
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <div
      ref={dragRef as unknown as React.Ref<HTMLDivElement>}
      className={cn(className, isDragging && "opacity-50 scale-95")}
      style={{ touchAction: draggable ? 'pan-y' : undefined }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      {children}
    </div>
  );
};
